import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import globalEn from './locales/en/translation.json';

const dynamicTranslationLoaders = {
  es: () => import('./locales/es/translation.json'),
  fr: () => import('./locales/fr/translation.json'),
};

type SupportedLanguage = 'en' | keyof typeof dynamicTranslationLoaders;

const supportedLanguages = new Set<SupportedLanguage>(['en', 'es', 'fr']);
const loadedLanguages = new Set<SupportedLanguage>(['en']);
let languageRequestId = 0;

const getSupportedLanguage = (language: string): SupportedLanguage => {
  const baseLanguage = language
    .trim()
    .replace('_', '-')
    .split('-')[0]
    .toLowerCase();

  if (supportedLanguages.has(baseLanguage as SupportedLanguage)) {
    return baseLanguage as SupportedLanguage;
  }

  return 'en';
};

const loadTranslation = async (language: SupportedLanguage) => {
  if (language === 'en' || loadedLanguages.has(language)) return;

  const { default: translation } = await dynamicTranslationLoaders[language]();
  i18n.addResourceBundle(language, 'translation', translation, true, true);
  loadedLanguages.add(language);
};

i18n.use(initReactI18next).init({
  resources: { en: { translation: globalEn } },
  lng: 'en',
  fallbackLng: 'en',
  ns: ['translation'],
  defaultNS: 'translation',
  initAsync: false,
  interpolation: { escapeValue: false },
});

export const loadLanguage = async (language?: string) => {
  const requestId = ++languageRequestId;
  const requestedLanguage = language?.trim().replace('_', '-') || 'en';
  const supportedLanguage = getSupportedLanguage(requestedLanguage);

  try {
    await loadTranslation(supportedLanguage);
    if (requestId !== languageRequestId) return;

    await i18n.changeLanguage(
      supportedLanguage === 'en' &&
        !requestedLanguage.toLowerCase().startsWith('en')
        ? 'en'
        : requestedLanguage
    );
  } catch {
    if (requestId !== languageRequestId) return;

    await i18n.changeLanguage('en');
  }
};

export default i18n;
